/** What makes the store builds behave like a phone app rather than a website
 *  in a frame. Started once from `main.ts`; does nothing on the website.
 *
 *  - **Back** (Android's back gesture or button) closes what is open first,
 *    exactly as Escape does (a dialog, a popover, a tour), then goes back
 *    through the tabs the reader visited, and at the first screen sends the
 *    app to the background, as Android apps do. Without this the shell's
 *    default closes the whole app on the first press, mid-dialog.
 *  - **Links into the app**: the store listing's Open-App link
 *    (`disciplesbible://open/...`) and, once the domain verifies the app, a
 *    `https://thedisciplesbible.org/...` link, open that page here.
 *  - **The status bar** wears dark icons on a light theme and light icons on a
 *    dark one, following the app's own scheme rather than the phone's, and on
 *    Android the strip behind the bars wears the theme's own colour
 *    (`mobile/android/.../ShellPlugin.java`). */
import { nativePlatform, onNative, callNative, hasPlugin, learnStorefront, publicSiteBase } from './nativeApp';

/** The page inside the app that an outside link names, or null for a link
 *  that is not ours. Pure, so the smoke suite can drive it. */
export function localPathFor(url: string, site = publicSiteBase()): string | null {
  let u: URL;
  try { u = new URL(url); } catch { return null; }
  if (`${u.origin}/` === site) return `${u.pathname}${u.search}${u.hash}` || '/';
  if (u.protocol === 'disciplesbible:') {
    // disciplesbible://open/u/mary#x: the "host" is a verb, the rest is the page.
    const rest = u.pathname.replace(/^\/+/, '');
    return `/${rest}${u.search}${u.hash}`;
  }
  return null;
}

function goBack(canGoBack: boolean): void {
  // Escape first: every dialog, popover and tour in the app already closes on
  // it (ui/widgets.ts's layer stack, the tour's own claim), and each one says
  // it handled the key by preventing its default.
  const esc = new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true });
  (document.activeElement ?? document.body).dispatchEvent(esc);
  if (esc.defaultPrevented) return;
  if (canGoBack) { history.back(); return; }
  void callNative('App', 'minimizeApp').catch(() => undefined);
}

function openLink(url: string): void {
  const path = localPathFor(url);
  if (path === null) return;
  const here = `${location.pathname}${location.search}`;
  const target = new URL(path, location.origin);
  // A plan link (a reminder's, a widget's) opens the plan in place: the app
  // is already running, so reloading it to read `?plan=` would only cost the
  // reader their place. main.ts listens for the same event a cold start sends.
  const plan = target.searchParams.get('plan');
  const browse = target.searchParams.has('plans');
  if ((plan || browse) && target.pathname === location.pathname) {
    if (target.hash && target.hash !== location.hash) location.hash = target.hash;
    window.dispatchEvent(new CustomEvent('bea:open-plan', { detail: browse ? { presets: true } : { planId: plan } }));
    return;
  }
  if (`${target.pathname}${target.search}` === here) {
    if (target.hash && target.hash !== location.hash) location.hash = target.hash;
    return;
  }
  location.assign(target.href);
}

/** True when dark icons read better than light ones on `hex` (#rgb or
 *  #rrggbb): its contrast with black beats its contrast with white. Pure. */
export function isLightColor(hex: string): boolean {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return false;
  const h = m[1].length === 3 ? m[1].replace(/./g, '$&$&') : m[1];
  const lin = (i: number): number => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const l = 0.2126 * lin(0) + 0.7152 * lin(2) + 0.0722 * lin(4);
  return (l + 0.05) / 0.05 > 1.05 / (l + 0.05);
}

function followScheme(): void {
  const root = document.documentElement;
  let lastStyle = '';
  let lastColor = '';
  const paint = (): void => {
    // What sits behind the bars is the theme-color: the strip Android pads
    // (a web view older than 140, where Capacitor keeps the app off the bars)
    // takes it, and where the app draws under the bars itself that colour is
    // the painting's own top edge (theme/chromeTint.ts). So the icons are
    // chosen by ITS brightness, as a browser chooses them, never by the scheme:
    // a light theme's art can be dark at the top (Light over Cozy Cabin is
    // #6d5f59, where dark icons could not be read).
    const color = document.querySelector('meta[name="theme-color"]')?.getAttribute('content') ?? '';
    const lightGround = color ? isLightColor(color) : root.getAttribute('data-scheme') === 'light';
    // Capacitor's styles name the BACKGROUND: DARK means light icons.
    const style = lightGround ? 'LIGHT' : 'DARK';
    if (style !== lastStyle && hasPlugin('SystemBars')) {
      lastStyle = style;
      void callNative('SystemBars', 'setStyle', { style }).catch(() => undefined);
    }
    if (color && color !== lastColor && hasPlugin('Shell')) {
      lastColor = color;
      void callNative('Shell', 'setBackground', { color }).catch(() => undefined);
    }
  };
  paint();
  const watch = new MutationObserver(paint);
  watch.observe(root, { attributes: true, attributeFilter: ['data-scheme'] });
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) watch.observe(meta, { attributes: true, attributeFilter: ['content'] });
}

let started = false;

export function initNativeShell(): void {
  if (started || !nativePlatform()) return;
  started = true;
  document.documentElement.classList.add('native-app');
  if (nativePlatform() === 'android') {
    onNative('App', 'backButton', (d) => goBack(d.canGoBack === true));
  }
  onNative('App', 'appUrlOpen', (d) => { if (typeof d.url === 'string') openLink(d.url); });
  // A cold start from a link: the shell holds the URL that launched it.
  void callNative<{ url?: string }>('App', 'getLaunchUrl')
    .then((r) => { if (r?.url) openLink(r.url); })
    .catch(() => undefined);
  followScheme();
  // Which App Store storefront this is, for the US-only link out (storeRules.ts).
  learnStorefront();
}
